import React from 'react';
import PropTypes from 'prop-types';

import { useNavigate } from 'react-router-dom';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Container, Stack, Typography } from '@mui/material';
import {
  ArrowRightIcon,
  FullLogo,
  UnsupportedIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { Button } from '@classity-edtech/ui-base';

export type NotFoundProps = MuiBoxProps;

const NotFound = ({ className }: NotFoundProps) => {
  const navigate = useNavigate();

  const navigateHome = () => {
    navigate('/');
  };

  return (
    <Container className={className} sx={{ px: { xs: 2.5, md: 2 } }}>
      <Box sx={{ py: { xs: 1, md: 2 } }}>
        <FullLogo
          sx={{ width: { xs: '82px', md: '140px' }, height: 'auto' }}
          onClick={navigateHome}
        />
      </Box>
      <Stack
        sx={{
          width: { xs: '100%', md: '568px' },
          my: { xs: 5, md: 10 },
          mx: 'auto',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <UnsupportedIcon sx={{ width: '170px', height: '130px' }} />
        <Typography variant='subtitle3' sx={{ mt: 3 }}>
          Page Not Found
        </Typography>
        <Typography variant='body2' sx={{ mb: 3 }}>
          Sorry, the content you are looking for doesn't exist. Either the content has been removed
          or you mistyped the link.
        </Typography>
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            gap: 0.5,
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={navigateHome}
        >
          <Typography variant='body3'>Back to home</Typography>
          <ArrowRightIcon sx={{ width: '16px', height: '16px' }} />
        </Box>
        <Button sx={{ display: { xs: 'flex', md: 'none' } }} onClick={navigateHome} showRightIcon>
          <Typography variant='body2SemiBold' sx={{ textTransform: 'uppercase' }}>
            Home
          </Typography>
        </Button>
      </Stack>
    </Container>
  );
};

NotFound.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

NotFound.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(NotFound)(
  ({ theme }) => `
  &.MuiBox-root {
    background-color: ${theme.palette.background.paper};
    height: 100vh;
    width: 100vw;
    display: flex;
    position: relative;
    text-align: center;
    align-items: flex-end;
    justify-content: center;

    & .MuiTypography-h1Rockwell {
      ${theme.breakpoints.between('md', 'xl')} {
        font-size: 4.2rem; // TODO: Remove when text responsiveness is resolved
      }

      ${theme.breakpoints.down('md')} {
        font-size: 36px; // TODO: Remove when text responsiveness is resolved
      }
    }

    & .MuiTypography-root {
      font-family: ${theme.typography.gillSans.regular};
    }

    & .MuiTypography-subtitle2 {
      font-family: ${theme.typography.gillSans.semiBold};
    }

    & .not-found__image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  
    & img {
      max-width: 1140px;
      max-height: 570px;
      z-index: 1;
      object-fit: cover;
    }

    & .MuiTypography-h2Rockwell {  
      ${theme.breakpoints.between('md', 'xl')} {
        & {
          font-size: 2.5rem; // TODO: Remove when text responsiveness is resolved
        }
      }
  
      ${theme.breakpoints.down('md')} {
        & {
          font-size: 28px; // TODO: Remove when text responsiveness is resolved
          line-height: 35px;
        }
      }
    }

    ${theme.breakpoints.down('lg')} {
      & img {
        max-width: 90vw;
      }
    }
  }
`,
);
